import { useMemo, useState } from 'react';
import { Content, PageWithHeader } from '@backstage/core-components';
import { Box, Chip, Tab, Tabs, Tooltip, Typography } from '@material-ui/core';
import {
  JOURNEYS,
  resolveEdge,
  resolveExternals,
  resolveSteps,
} from './journeys';
import { Storyboard } from './Storyboard';
import { teamColor } from './teamColors';
import { ExternalSystem, Flow } from './types';

const TeamLegend = ({
  teams,
  activeTeam,
  onSelect,
}: {
  teams: string[];
  activeTeam?: string;
  onSelect: (team?: string) => void;
}) => (
  <Box
    display="flex"
    alignItems="center"
    flexWrap="wrap"
    mb={2}
    style={{ gap: 8 }}
  >
    <Typography variant="caption" color="textSecondary">
      Show one team
    </Typography>
    {teams.map(team => (
      <Chip
        key={team}
        size="small"
        label={team}
        variant={activeTeam === team ? 'default' : 'outlined'}
        onClick={() => onSelect(activeTeam === team ? undefined : team)}
        style={{ borderColor: teamColor(team), borderWidth: 2 }}
      />
    ))}
  </Box>
);

const ExternalSystems = ({
  systems,
}: {
  systems: (ExternalSystem & { id: string })[];
}) =>
  systems.length === 0 ? null : (
    <Box mt={3}>
      <Typography variant="subtitle2" gutterBottom>
        External systems
      </Typography>
      <Box display="flex" flexWrap="wrap" style={{ gap: 8 }}>
        {systems.map(system => (
          <Tooltip key={system.id} title={system.desc ?? ''} arrow>
            <Chip variant="outlined" label={system.name} />
          </Tooltip>
        ))}
      </Box>
    </Box>
  );

/** Which journey is open, which service is hovered and which team is shown. */
const useJourneyView = () => {
  const [journeyId, setJourneyId] = useState(JOURNEYS[0].id);
  const [activeId, setActiveId] = useState<string>();
  const [activeTeam, setActiveTeam] = useState<string>();
  const journey = JOURNEYS.find(it => it.id === journeyId) ?? JOURNEYS[0];
  const steps = useMemo(() => resolveSteps(journey.flow), [journey]);
  const nodes = steps.flatMap(step => Object.values(step.lanes).flat());
  const teams = [...new Set(nodes.map(node => node.team))].sort();
  const openJourney = (id: string) => {
    setJourneyId(id);
    setActiveTeam(undefined);
  };
  return {
    journey,
    steps,
    teams,
    activeId,
    setActiveId,
    activeTeam,
    setActiveTeam,
    openJourney,
  };
};

/** The title and lede of the open journey. */
const JourneyIntro = ({ flow }: { flow: Flow }) => (
  <Box my={2}>
    <Typography variant="h6">{flow.meta.title}</Typography>
    {flow.meta.lede && (
      <Typography color="textSecondary">{flow.meta.lede}</Typography>
    )}
  </Box>
);

/** The customer journey as a storyboard: each step, and every service behind it. */
export const JourneysPage = () => {
  const view = useJourneyView();
  const { flow } = view.journey;

  return (
    <PageWithHeader
      title="Journeys"
      subtitle="How a customer moves through ATG, and the services behind each step"
      themeId="home"
    >
      <Content>
        <Tabs
          value={view.journey.id}
          onChange={(_, id) => view.openJourney(id)}
        >
          {JOURNEYS.map(it => (
            <Tab key={it.id} value={it.id} label={it.label} />
          ))}
        </Tabs>
        <JourneyIntro flow={flow} />
        <TeamLegend
          teams={view.teams}
          activeTeam={view.activeTeam}
          onSelect={view.setActiveTeam}
        />
        <Storyboard
          steps={view.steps}
          edge={resolveEdge(flow)}
          activeId={view.activeId}
          activeTeam={view.activeTeam}
          onHover={view.setActiveId}
        />
        <ExternalSystems systems={resolveExternals(flow)} />
      </Content>
    </PageWithHeader>
  );
};
